import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import { formatRelativeTimeAgo } from '../../utils/relativeTime'
import { resolveIconNode } from '../../components/icons'

function formatRelativeTime(date) {
  if (!date) return '—'
  const ago = formatRelativeTimeAgo(date)
  if (ago === 'just now') return 'Just now'
  return ago
}

function regStatus(createdAt) {
  if (!createdAt) return 'pending'
  const hours = (Date.now() - new Date(createdAt).getTime()) / 3600000
  return hours < 24 ? 'new' : 'pending'
}

function phaseClass(phase) {
  const p = (phase || '').toLowerCase()
  if (p.includes('nda') || p.includes('marketing')) return 'pending'
  if (p.includes('dd') || p.includes('term')) return 'approved'
  return 'new'
}

export default function AdminDashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [pendingRegs, setPendingRegs] = useState([])
  const [activeDeals, setActiveDeals] = useState([])
  const [alerts, setAlerts] = useState([])
  const [revenueData, setRevenueData] = useState([])
  const [pendingPayments, setPendingPayments] = useState([])

  const load = async () => {
    try {
      const [statsRes, regsRes, dealsRes, alertsRes, revenueRes, paymentsRes] = await Promise.all([
        axios.get('/api/admin/dashboard-stats'),
        axios.get('/api/admin/pending-registrations'),
        axios.get('/api/admin/active-deals'),
        axios.get('/api/admin/system-alerts'),
        axios.get('/api/admin/revenue-analytics'),
        axios.get('/api/admin/payments').catch(() => ({ data: { pendingOrders: [] } })),
      ])
      setStats(statsRes.data)
      setPendingRegs(regsRes.data)
      setActiveDeals(dealsRes.data)
      setAlerts(alertsRes.data)
      setRevenueData(revenueRes.data.monthly || revenueRes.data || [])
      setPendingPayments(paymentsRes.data?.pendingOrders || [])
    } catch {
      toast.error('Failed to load admin dashboard')
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleApprove = async (userId, decision) => {
    try {
      await axios.post(`/api/admin/registration-approval/${userId}`, { decision })
      toast.success(decision === 'approved' ? 'Approved' : 'Rejected')
      load()
    } catch {
      toast.error('Action failed')
    }
  }

  const maxBar = Math.max(...revenueData.map((d) => d.revenue || 0), 1)

  return (
    <AdminLayout variant="dashboard">
      <div className="admin-stats-row">
        {[
          {
            label: 'Total Users',
            value: stats?.total_users?.toLocaleString() || '0',
            change: `↑ ${stats?.new_users_this_month || 0} this month`,
            icon: 'users',
            iconClass: 'blue',
          },
          {
            label: 'Active Deals',
            value: stats?.active_deals || '0',
            change: `↑ ${stats?.new_deals_this_week || 0} new this week`,
            icon: 'chart',
            iconClass: 'gold',
          },
          {
            label: 'Total Escrowed',
            value: `$${((stats?.total_escrowed || 0) / 1e6).toFixed(1)}M`,
            change: stats?.pending_escrow_events ? `$${stats.pending_escrow_events} pending wire(s)` : 'All active deals',
            icon: 'dollar',
            iconClass: 'green',
          },
          {
            label: 'Platform Revenue',
            value: `$${((stats?.platform_revenue || 0) / 1e6).toFixed(2)}M`,
            change: stats?.revenue_subtitle || `${stats?.active_subscriptions ?? 0} active subscription(s)`,
            icon: 'dollar',
            iconClass: 'red',
          },
        ].map((s) => (
          <div key={s.label} className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">{s.label}</span>
              <span className={`admin-stat-icon ${s.iconClass}`}>{resolveIconNode(s.icon, { size: 'md' })}</span>
            </div>
            <div className="admin-stat-value">{s.value}</div>
            <div className="admin-stat-change up">{s.change}</div>
          </div>
        ))}
      </div>

      {pendingPayments.length > 0 && (
        <div className="admin-card" style={{ marginBottom: 24, borderColor: '#f59e0b' }}>
          <div className="admin-card-header">
            <span className="admin-card-title">Payment confirmation pending ({pendingPayments.length})</span>
            <button type="button" className="admin-card-action" onClick={() => navigate('/admin/payments')}>
              Review all →
            </button>
          </div>
          <div className="admin-card-body flush">
            <table className="admin-data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Company</th>
                  <th>Period</th>
                  <th>Amount</th>
                  <th>Reported</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingPayments.slice(0, 5).map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.system_name}</strong>
                      <div style={{ fontSize: 11, color: '#909399' }}>{item.work_email}</div>
                    </td>
                    <td>{item.company_name || '—'}</td>
                    <td><strong>{item.period_label || '—'}</strong></td>
                    <td>{item.amount_display}</td>
                    <td>{formatRelativeTime(item.updated_at || item.created_at)}</td>
                    <td className="admin-td-actions">
                      <div className="admin-action-btns">
                      <button
                        type="button"
                        className="admin-abtn admin-abtn-approve"
                        onClick={() => navigate(`/admin/payments?review=${item.id}`)}
                      >
                        Review & approve
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="admin-content-grid">
        <div>
          <div className="admin-card">
            <div className="admin-card-header">
              <span className="admin-card-title">Pending GP Reviews</span>
              <button type="button" className="admin-card-action" onClick={() => navigate('/admin/registration')}>
                View All →
              </button>
            </div>
            <div className="admin-card-body flush">
              <table className="admin-data-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>Type</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRegs.slice(0, 5).map((item) => {
                    const st = regStatus(item.created_at || item.event_at)
                    return (
                      <tr key={item.id}>
                        <td><strong>{item.company_name || '—'}</strong></td>
                        <td>{item.display_name || item.system_name}</td>
                        <td>{item.role || 'GP'}</td>
                        <td>{formatRelativeTime(item.created_at || item.event_at)}</td>
                        <td>
                          <span className={`admin-status-dot ${st === 'new' ? 'new' : 'pending'}`}>
                            {st === 'new' ? 'New' : 'Pending'}
                          </span>
                        </td>
                        <td className="admin-td-actions">
                          <div className="admin-action-btns">
                            <button type="button" className="admin-abtn admin-abtn-view" onClick={() => navigate('/admin/registration')}>Review</button>
                            <button type="button" className="admin-abtn admin-abtn-approve" onClick={() => handleApprove(item.id, 'approved')}>Approve</button>
                            <button type="button" className="admin-abtn admin-abtn-reject" onClick={() => navigate('/admin/registration')}>Reject</button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                  {!pendingRegs.length && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#909399', padding: 24 }}>No pending registrations</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="admin-card">
            <div className="admin-card-header">
              <span className="admin-card-title">Active Deals Monitor</span>
              <button type="button" className="admin-card-action" onClick={() => navigate('/admin/pipeline')}>Full List →</button>
            </div>
            <div className="admin-card-body flush">
              <table className="admin-data-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>GP</th>
                    <th>Target</th>
                    <th>LPs</th>
                    <th>Escrowed</th>
                    <th>Phase</th>
                  </tr>
                </thead>
                <tbody>
                  {activeDeals.map((item) => (
                    <tr key={item.id}>
                      <td><strong>{item.project}</strong></td>
                      <td>{item.gp}</td>
                      <td>{item.target}</td>
                      <td>{item.lps}</td>
                      <td>{item.escrowed}</td>
                      <td><span className={`admin-status-dot ${phaseClass(item.phase)}`}>{item.phase}</span></td>
                    </tr>
                  ))}
                  {!activeDeals.length && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#909399', padding: 24 }}>No active deals</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div>
          <div className="admin-card">
            <div className="admin-card-header">
              <span className="admin-card-title">Subscription Revenue (6 mo.)</span>
            </div>
            <div className="admin-card-body">
              <div className="admin-revenue-chart">
                {revenueData.map((data, idx) => {
                  const total = data.revenue || 0
                  return (
                    <div key={idx} className="admin-rc-bar-group">
                      <div className="admin-rc-stack" style={{ height: `${Math.max(20, (total / maxBar) * 140)}px` }}>
                        {total > 0 ? (
                          <div className="admin-rc-bar admin-rc-bar-subs" style={{ flex: 1 }} />
                        ) : (
                          <div className="admin-rc-bar admin-rc-bar-email" style={{ height: 20, opacity: 0.35 }} />
                        )}
                      </div>
                      <div className="admin-rc-label">{data.month}</div>
                      <div className="admin-rc-val">
                        {total >= 1000 ? `$${Math.round(total / 1000)}K` : total > 0 ? `$${Math.round(total)}` : '—'}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="admin-rc-legend">
                <span><span className="admin-rc-dot subs" /> Membership subscriptions (from database)</span>
              </div>
            </div>
          </div>

          <div className="admin-card">
            <div className="admin-card-header">
              <span className="admin-card-title">System Alerts</span>
            </div>
            <div className="admin-card-body">
              <ul className="admin-alert-list">
                {alerts.map((alert, idx) => (
                  <li
                    key={idx}
                    className={`admin-alert-item${alert.link ? ' clickable' : ''}`}
                    onClick={alert.link ? () => navigate(alert.link) : undefined}
                    onKeyDown={alert.link ? (e) => e.key === 'Enter' && navigate(alert.link) : undefined}
                    role={alert.link ? 'button' : undefined}
                    tabIndex={alert.link ? 0 : undefined}
                  >
                    <span className={`admin-alert-icon ${alert.type || 'info'}`}>{alert.icon}</span>
                    <div>
                      <div className="admin-alert-text"><strong>{alert.title}:</strong> {alert.desc}</div>
                      <div className="admin-alert-time">{alert.time}{alert.link ? ' · Click to open' : ''}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="admin-card">
            <div className="admin-card-header">
              <span className="admin-card-title">Quick Actions</span>
            </div>
            <div className="admin-card-body">
              <div className="admin-quick-grid">
                <button type="button" className="admin-quick-btn" onClick={() => navigate('/admin/users')}>+ Add User</button>
                <button type="button" className="admin-quick-btn" onClick={() => navigate('/admin/listings')}>Review Doc</button>
                <button type="button" className="admin-quick-btn" onClick={() => navigate('/admin/pipeline')}>Confirm Wire</button>
                <button type="button" className="admin-quick-btn" onClick={() => navigate('/admin/messages')}>Send Notice</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
